import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

const Career = () => {
  // ✅ Production endpoint
  const API_URL = "https://themps.global/api/career-mail";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    experience: "",
    position: "",
    date: "",
    resume: null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // ✅ Resume file
    if (name === "resume") {
      setFormData({ ...formData, resume: files?.[0] || null });
      return;
    }

    // ✅ Phone: digits only + max 10
    if (name === "phone") {
      const onlyNumbers = value.replace(/\D/g, "");
      if (onlyNumbers.length <= 10) {
        setFormData({ ...formData, phone: onlyNumbers });
      }
      return;
    }

    // ✅ Name: letters + space only
    if (name === "name") {
      const onlyLetters = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({ ...formData, name: onlyLetters });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Full Name is required";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email))
      newErrors.email = "Email is invalid";

    if (!formData.phone) newErrors.phone = "Phone number is required";
    else if (formData.phone.length !== 10)
      newErrors.phone = "Phone number must be 10 digits";

    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.experience) newErrors.experience = "Experience is required";
    if (!formData.position) newErrors.position = "Position is required";
    if (!formData.date) newErrors.date = "Birth date is required";
    if (!formData.resume) newErrors.resume = "Resume is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      gender: "",
      experience: "",
      position: "",
      date: "",
      resume: null,
    });

    const formEl = document.getElementById("careerForm");
    if (formEl) formEl.reset();

    setErrors({});
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });

    try {
      const res = await axios.post(API_URL, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setLoading(false);

      if (res?.data?.status === "success") {
        Swal.fire({
          title: "Application Submitted!",
          text: "Thank you for applying. Our team will contact you soon.",
          icon: "success",
          confirmButtonColor: "#3085d6",
        });
        resetForm();
      } else {
        Swal.fire({
          title: "Failed!",
          text: res?.data?.message || "Something went wrong. Try again!",
          icon: "error",
        });
      }
    } catch (error) {
      setLoading(false);
      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message ||
          "Unable to submit the application.",
        icon: "error",
      });
    }
  };

  return (
    <>
      <header className="site-header site-news-header">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 col-12 mx-auto">
              <h1 className="text-white">We Are Hiring!</h1>
              <strong className="text-white">
                Our Company Needs Fresh Minds!
              </strong>
            </div>
          </div>
        </div>

        <div className="video-wrap">
          <video autoPlay loop muted className="custom-video">
            <source src="video/career_tiny.mp4" type="video/mp4" />
          </video>
        </div>

        <div className="overlay"></div>
      </header>

      <section className="news-detail section-padding pt-0">
        <div className="container">
          <h4 className="mb-lg-4">Apply Here</h4>

          <form
            className="booking-form row"
            id="careerForm"
            onSubmit={handleSubmit}
          >
            {/* Full Name */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <span className="text-danger">{errors.name}</span>
              )}
            </div>

            {/* Email */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
              />
              {errors.email && (
                <span className="text-danger">{errors.email}</span>
              )}
            </div>

            {/* Phone */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Phone Number</label>
              <input
                type="text"
                name="phone"
                className="form-control"
                value={formData.phone}
                onChange={handleChange}
                maxLength={10}
                placeholder="Enter your phone number"
                onKeyDown={(e) => {
                  if (
                    !(
                      (e.key >= "0" && e.key <= "9") ||
                      e.key === "Backspace" ||
                      e.key === "Tab" ||
                      e.key === "ArrowLeft" ||
                      e.key === "ArrowRight" ||
                      e.key === "Delete"
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
              />
              {errors.phone && (
                <span className="text-danger">{errors.phone}</span>
              )}
            </div>

            {/* Gender */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Gender</label>
              <select
                name="gender"
                className="form-control"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && (
                <span className="text-danger">{errors.gender}</span>
              )}
            </div>

            {/* Experience */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Experience</label>
              <select
                name="experience"
                className="form-control"
                value={formData.experience}
                onChange={handleChange}
              >
                <option value="">Choose</option>
                <option value="Fresher">Fresher</option>
                <option value="1-5">1 - 5 Years</option>
                <option value="5-7">5 - 7 Years</option>
                <option value="7+">7+ Years</option>
              </select>
              {errors.experience && (
                <span className="text-danger">{errors.experience}</span>
              )}
            </div>

            {/* Position */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Position</label>
              <select
                name="position"
                className="form-control"
                value={formData.position}
                onChange={handleChange}
              >
                <option value="">Select Position</option>
                <option value="Company Secretary">Company Secretary</option>
                <option value="CA">Chartered Accountant</option>
                <option value="Advocate">Advocate</option>
              </select>
              {errors.position && (
                <span className="text-danger">{errors.position}</span>
              )}
            </div>

            {/* Birth Date */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Birth Date</label>
              <input
                type="date"
                name="date"
                className="form-control"
                value={formData.date}
                onChange={handleChange}
              />
              {errors.date && (
                <span className="text-danger">{errors.date}</span>
              )}
            </div>

            {/* Resume */}
            <div className="col-lg-6 col-12">
              <label className="form-label">Resume</label>
              <input
                type="file"
                name="resume"
                className="form-control"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleChange}
              />
              {errors.resume && (
                <span className="text-danger">{errors.resume}</span>
              )}
            </div>

            {/* Submit button aligned right */}
            <div className="col-12 mt-4 d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-primary py-3 px-4"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default Career;
